import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Plus,
  Search,
  Calendar,
  DollarSign,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  Eye,
  Edit,
  Trash2,
} from "lucide-react";
import apiClient from "@/api/client";
import { toastHelpers } from "@/lib/toast-helpers";
import { ExpenseForm } from "@/components/forms/ExpenseForm";
import { PaginationControlsComponent } from "@/components/ui/pagination-controls";
import { usePagination } from "@/hooks/usePagination";
import { InlineLoading } from "@/components/ui/loading-spinner";
import type { Expense } from "@/types";
import { useRouter } from "@tanstack/react-router";
import { useNavigationRefresh } from "@/hooks/useNavigationRefresh";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export function AdminExpensesManagement() {
  const { t } = useTranslation();
  const router = useRouter();
  const { manualRefresh, isRefreshing } = useNavigationRefresh(["expenses"]);
  const queryClient = useQueryClient();

  useEffect(() => {
    const decodedToken = apiClient.isAuthenticated();
    if (!decodedToken) {
      toastHelpers.sessionExpired();
      router.navigate({ to: "/login" });
    }
  }, []);

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [dateRangeFilter, setDateRangeFilter] = useState<string>("all");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);

  // Get current user info for permission checks
  const currentUser = apiClient.isAuthenticated();
  const isAdmin = currentUser?.role === "admin";
  const isManager = currentUser?.role === "manager";
  const canApprove = isAdmin || isManager;

  // Pagination hook
  const pagination = usePagination({
    initialPage: 1,
    initialPageSize: 20,
    total: 0,
  });

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      pagination.goToFirstPage();
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Helper function to get date range based on filter
  const getDateRange = (filter: string) => {
    const now = new Date();
    let startDate: Date | undefined;
    let endDate: Date | undefined;

    switch (filter) {
      case "today":
        startDate = new Date(now.setHours(0, 0, 0, 0));
        endDate = new Date(now.setHours(23, 59, 59, 999));
        break;
      case "week":
        const weekStart = new Date(now);
        weekStart.setDate(now.getDate() - now.getDay()); // Start of week (Sunday)
        weekStart.setHours(0, 0, 0, 0);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6); // End of week (Saturday)
        weekEnd.setHours(23, 59, 59, 999);
        startDate = weekStart;
        endDate = weekEnd;
        break;
      case "month":
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date(
          now.getFullYear(),
          now.getMonth() + 1,
          0,
          23,
          59,
          59,
          999
        );
        break;
      default:
        return {};
    }

    return {
      startDate: startDate?.toISOString().split("T")[0],
      endDate: endDate?.toISOString().split("T")[0],
    };
  };

  // Fetch expenses
  const { data: expensesData, isLoading } = useQuery({
    queryKey: [
      "expenses",
      pagination.page,
      pagination.pageSize,
      debouncedSearch,
      statusFilter,
      categoryFilter,
      dateRangeFilter,
    ],
    queryFn: () => {
      const dateRange = getDateRange(dateRangeFilter);
      console.log("dataRangeFilter:", dateRangeFilter, dateRange);
      return apiClient
        .getExpenses({
          page: pagination.page,
          per_page: pagination.pageSize,
          search: debouncedSearch,
          status: statusFilter !== "all" ? statusFilter : undefined,
          category: categoryFilter !== "all" ? categoryFilter : undefined,
          ...dateRange,
        })
        .then((res: any) => {
          console.log("Fetched expenses:", res);
          return res;
        });
    },
  });

  // Fetch expense summary
  const { data: summaryData } = useQuery({
    queryKey: ["expenses", "summary", "month"],
    queryFn: () => {
      return apiClient.getExpenseSummary().then((res: any) => {
        console.log("Fetched expense summary:", res);
        return res.data;
      });
    },
  });

  const isLoadingAny = isRefreshing || isLoading;

  const paginationInfo = (expensesData as any)?.pagination || { total: 0 };
  const expenses = (expensesData as any)?.expenses || [];
  const summary = summaryData || {
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    totalAmount: 0,
    approvedAmount: 0,
    pendingAmount: 0,
  };

  // Delete expense mutation
  const deleteExpenseMutation = useMutation({
    mutationFn: (id: string) => apiClient.deleteExpense(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["expenses"] });
      toastHelpers.success(t("Toast.Expense.DeleteSuccessMessage"));
    },
    onError: () => {
      toastHelpers.error(t("Toast.Expense.DeleteError"));
    },
  });

  // Approve expense mutation
  const approveExpenseMutation = useMutation({
    mutationFn: (id: string) => apiClient.approveExpense(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["expenses"] });
      toastHelpers.success(t("Toast.Expense.ApproveSuccessMessage"));
    },
    onError: () => {
      toastHelpers.error(t("Toast.Expense.ApproveError"));
    },
  });

  // Reject expense mutation
  const rejectExpenseMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      apiClient.rejectExpense(id, { rejection_reason: reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["expenses"] });
      toastHelpers.success(t("Toast.Expense.RejectSuccessMessage"));
    },
    onError: () => {
      toastHelpers.error(t("Toast.Expense.RejectError"));
    },
  });

  const handleFormSuccess = () => {
    setShowCreateForm(false);
    setEditingExpense(null);
  };

  const handleCancelForm = () => {
    setShowCreateForm(false);
    setEditingExpense(null);
  };

  const handleDeleteExpense = (expense: Expense) => {
    if (
      confirm(
        t("AdminExpensesManagement.Confirm.DeleteExpense", {
          description: expense.description,
        })
      )
    ) {
      deleteExpenseMutation.mutate(expense.id.toString());
    }
  };

  const handleApproveExpense = (expense: Expense) => {
    if (
      confirm(
        t("AdminExpensesManagement.Confirm.ApproveExpense", {
          description: expense.description,
        })
      )
    ) {
      approveExpenseMutation.mutate(expense.id.toString());
    }
  };

  const handleRejectExpense = (expense: Expense) => {
    const reason = prompt(t("AdminExpensesManagement.Confirm.RejectReason"));
    if (reason && reason.trim()) {
      rejectExpenseMutation.mutate({ id: expense.id.toString(), reason });
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "approved":
        return (
          <Badge className="bg-green-100 text-green-800">
            <CheckCircle className="w-3 h-3 mr-1" />
            {t("AdminExpensesManagement.Status.Approved")}
          </Badge>
        );
      case "rejected":
        return (
          <Badge className="bg-red-100 text-red-800">
            <XCircle className="w-3 h-3 mr-1" />
            {t("AdminExpensesManagement.Status.Rejected")}
          </Badge>
        );
      default:
        return (
          <Badge className="bg-yellow-100 text-yellow-800">
            <Clock className="w-3 h-3 mr-1" />
            {t("AdminExpensesManagement.Status.Pending")}
          </Badge>
        );
    }
  };

  const formatCurrency = (amount: number) => {
    return `Rs ${amount}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const categoryOptions = [
    {
      value: "all",
      label: t("AdminExpensesManagement.Categories.AllCategories"),
    },
    {
      value: "utilities",
      label: t("AdminExpensesManagement.Categories.Utilities"),
    },
    { value: "rent", label: t("AdminExpensesManagement.Categories.Rent") },
    {
      value: "salaries",
      label: t("AdminExpensesManagement.Categories.Salaries"),
    },
    {
      value: "supplies",
      label: t("AdminExpensesManagement.Categories.Supplies"),
    },
    {
      value: "maintenance",
      label: t("AdminExpensesManagement.Categories.Maintenance"),
    },
    {
      value: "marketing",
      label: t("AdminExpensesManagement.Categories.Marketing"),
    },
    {
      value: "transportation",
      label: t("AdminExpensesManagement.Categories.Transportation"),
    },
    {
      value: "equipment",
      label: t("AdminExpensesManagement.Categories.Equipment"),
    },
    {
      value: "food_ingredients",
      label: t("AdminExpensesManagement.Categories.FoodIngredients"),
    },
    { value: "other", label: t("AdminExpensesManagement.Categories.Other") },
  ];

  if (showCreateForm || editingExpense) {
    return (
      <div className="p-6">
        <ExpenseForm
          expense={editingExpense || undefined}
          mode={editingExpense ? "edit" : "create"}
          onSuccess={handleFormSuccess}
          onCancel={handleCancelForm}
        />
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-foreground">
            {t("AdminExpensesManagement.Heading")}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t("AdminExpensesManagement.Subheading")}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={manualRefresh}
            disabled={isRefreshing}
          >
            <RefreshCw
              className={`w-4 h-4 mr-2 ${isRefreshing ? "animate-spin" : ""}`}
            />
            {t("AdminExpensesManagement.Refresh")}
          </Button>
          <Button size="sm" onClick={() => setShowCreateForm(true)}>
            <Plus className="w-4 h-4 mr-2" />
            {t("AdminExpensesManagement.AddExpense")}
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* TotalExpenses This month */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">
                  {t("AdminExpensesManagement.Summary.TotalExpenses")}
                </p>
                <p className="text-2xl font-bold">{summary.totalCount || 0}</p>
              </div>
              <DollarSign className="w-8 h-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        {/* Pending Expenses This Month */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">
                  {t("AdminExpensesManagement.Summary.Pending")}
                </p>
                <p className="text-2xl font-bold text-yellow-600">
                  {summary.pendingAmount || 0}
                </p>
              </div>
              <Clock className="w-8 h-8 text-yellow-600" />
            </div>
          </CardContent>
        </Card>
        {/* Approved Expenses This Month */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">
                  {t("AdminExpensesManagement.Summary.Approved")}
                </p>
                <p className="text-2xl font-bold text-green-600">
                  {summary.approvedAmount || 0}
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
        {/* TotalAmount This Month */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">
                  {t("AdminExpensesManagement.Summary.TotalAmount")}
                </p>
                <p className="text-xl font-bold">
                  {formatCurrency(summary.totalAmount || 0)}
                </p>
              </div>
              <DollarSign className="w-8 h-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={t(
                  "AdminExpensesManagement.Filters.SearchPlaceholder"
                )}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={dateRangeFilter} onValueChange={setDateRangeFilter}>
              <SelectTrigger>
                <SelectValue
                  placeholder={t(
                    "AdminExpensesManagement.Filters.FilterByDate"
                  )}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  {t("AdminExpensesManagement.Filters.AllTime")}
                </SelectItem>
                <SelectItem value="today">
                  {t("AdminExpensesManagement.Filters.Today")}
                </SelectItem>
                <SelectItem value="week">
                  {t("AdminExpensesManagement.Filters.ThisWeek")}
                </SelectItem>
                <SelectItem value="month">
                  {t("AdminExpensesManagement.Filters.ThisMonth")}
                </SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue
                  placeholder={t(
                    "AdminExpensesManagement.Filters.FilterByStatus"
                  )}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  {t("AdminExpensesManagement.Filters.AllStatuses")}
                </SelectItem>
                <SelectItem value="pending">
                  {t("AdminExpensesManagement.Filters.Pending")}
                </SelectItem>
                <SelectItem value="approved">
                  {t("AdminExpensesManagement.Filters.Approved")}
                </SelectItem>
                <SelectItem value="rejected">
                  {t("AdminExpensesManagement.Filters.Rejected")}
                </SelectItem>
              </SelectContent>
            </Select>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger>
                <SelectValue
                  placeholder={t(
                    "AdminExpensesManagement.Filters.FilterByCategory"
                  )}
                />
              </SelectTrigger>
              <SelectContent>
                {categoryOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Expenses Table/List */}
      {isLoadingAny ? (
        <Card>
          <CardContent className="p-8">
            <InlineLoading
              text={t("AdminExpensesManagement.LoadingExpenses")}
            />
          </CardContent>
        </Card>
      ) : expenses.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <p className="text-muted-foreground">
              {t("AdminExpensesManagement.NoExpenses")}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50 border-b">
                  <tr>
                    <th className="text-left p-4 font-semibold">
                      {t("AdminExpensesManagement.Table.Title")}
                    </th>
                    <th className="text-left p-4 font-semibold">
                      {t("AdminExpensesManagement.Table.Description")}
                    </th>
                    <th className="text-left p-4 font-semibold">
                      {t("AdminExpensesManagement.Table.Amount")}
                    </th>
                    <th className="text-left p-4 font-semibold">
                      {t("AdminExpensesManagement.Table.Category")}
                    </th>
                    <th className="text-left p-4 font-semibold">
                      {t("AdminExpensesManagement.Table.Date")}
                    </th>
                    <th className="text-left p-4 font-semibold">
                      {t("AdminExpensesManagement.Table.Status")}
                    </th>
                    <th className="text-left p-4 font-semibold">
                      {t("AdminExpensesManagement.Table.SubmittedBy")}
                    </th>
                    <th className="text-left p-4 font-semibold">
                      {t("AdminExpensesManagement.Table.ApprovedBy")}
                    </th>
                    <th className="text-right p-4 font-semibold">
                      {t("AdminExpensesManagement.Table.Actions")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {expenses.map((expense: Expense) => (
                    <tr key={expense.id} className="border-b hover:bg-muted/30">
                      <td className="p-4">
                        <p className="font-semibold">{expense.title}</p>
                      </td>
                      <td className="p-4">
                        <p className="font-semibold">{expense.description}</p>
                      </td>

                      <td className="p-4">
                        <span className="font-semibold">
                          {formatCurrency(expense.amount)}
                        </span>
                      </td>
                      <td className="p-4">
                        <Badge variant="outline">{expense.category}</Badge>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar className="w-4 h-4" />
                          {formatDate(expense.expense_date)}
                        </div>
                      </td>
                      <td className="p-4">{getStatusBadge(expense.status)}</td>
                      <td className="p-4">
                        <p className="text-sm">
                          {expense.Submitter?.name || "Unknown"}
                        </p>
                      </td>
                      <td className="p-4">
                        <p className="text-sm">
                          {expense.status === "approved" &&
                          expense.Approver?.name
                            ? expense.Approver.name
                            : expense.status === "rejected"
                              ? t("AdminExpensesManagement.Table.RejectedLabel")
                              : t("AdminExpensesManagement.Table.NotApproved")}
                        </p>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-2">
                          {expense.status === "pending" && canApprove && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleApproveExpense(expense)}
                                className="text-green-600 hover:bg-green-50"
                              >
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleRejectExpense(expense)}
                                className="text-red-600 hover:bg-red-50"
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                          {expense.status === "pending" && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setEditingExpense(expense)}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                          )}
                          {expense.receipt_url && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() =>
                                window.open(expense.receipt_url, "_blank")
                              }
                              title="View receipt"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteExpense(expense)}
                            className="text-destructive hover:bg-destructive/10"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pagination */}
      {!isLoadingAny && expenses.length > 0 && (
        <PaginationControlsComponent
          pagination={pagination}
          total={paginationInfo.totalPages}
        />
      )}
    </div>
  );
}
